/**
 * Consolidated StatusProvider - Core Authentication & Authorization Context
 * Following Tim-Combo frontend auth pattern (MANDATORY)
 * Reference: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 *
 * Manages:
 * - Cognito authentication (DIRECT - no custom endpoints)
 * - User entitlements from API
 * - Super admin state and routing
 * - Client context switching for super admins
 *
 * NOTE: This provider requires:
 * - A Cognito UserPool instance to be passed as a prop
 * - User and UserEntitlement types to be provided
 * - An API client function (Make_Authorized_API_Call)
 */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUser, AuthenticationDetails, CognitoUserPool } from 'amazon-cognito-identity-js';

export interface StatusState {
  Is_Super_Admin: boolean;
  User_Entitlements: any[];
  Base_User_Entitlements: any[];
  Is_Loading: boolean;
  Auth_Status: boolean;
  Company_ID: string | null;
  Client_ID: string | null;
  Client_Name: string | null;
  Email_Address: string | null;
  Has_Client_Context: boolean;
  Error_Message: string | null;
}

export interface StatusContextValue extends StatusState {
  authenticate: (email: string, password: string) => Promise<void>;
  logout: () => void;
  Check_User_Status: (email: string) => Promise<void>;
  Set_Client_Context?: (clientId: string, companyId: string, options?: { navigate?: boolean }) => Promise<void>;
  Clear_Client_Context?: () => void;
}

const StatusContext = createContext<StatusContextValue | null>(null);

/**
 * Hook to use status context
 */
export function useStatus(): StatusContextValue {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within StatusProvider');
  }
  return context;
}

export interface StatusProviderProps {
  children: ReactNode;
  userPool: CognitoUserPool | null;
  apiClient: {
    getUser: (params: { Email_Address: string }) => Promise<any>;
    getUserEntitlements: (params: { Email_Address: string }) => Promise<any>;
    getClient?: (params: { id: string }) => Promise<any>;
  };
  tokenStorageKey?: 'token' | 'idToken';
  enableClientContext?: boolean;
}

/**
 * StatusProvider Component
 * Core authentication and authorization provider following Tim-Combo pattern
 */
export function StatusProvider({
  children,
  userPool,
  apiClient,
  tokenStorageKey = 'token',
  enableClientContext = false
}: StatusProviderProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<StatusState>({
    Is_Super_Admin: false,
    User_Entitlements: [],
    Base_User_Entitlements: [],
    Is_Loading: false,
    Auth_Status: false,
    Company_ID: null,
    Client_ID: null,
    Client_Name: null,
    Email_Address: null,
    Has_Client_Context: false,
    Error_Message: null,
  });

  /**
   * Check user status and load entitlements from API
   * Called after Cognito authentication succeeds
   */
  const Check_User_Status = useCallback(async (userEmail: string) => {
    console.log('[StatusProvider] Fetching user data and entitlements for:', userEmail);

    // Wait a moment to ensure auth token is ready (Tim-Combo pattern)
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Get user data from API
    const userResponse = await apiClient.getUser({ Email_Address: userEmail });

    if (!userResponse.success) {
      throw new Error(userResponse.message || 'Failed to get user details');
    }

    const user = userResponse.data.Records[0];
    const Is_Super = Boolean(user?.Super_Admin);

    console.log('[StatusProvider] User data fetched, Is_Super_Admin:', Is_Super);

    // Super admins don't need entitlements - they have global access
    if (Is_Super) {
      console.log('[StatusProvider] Super admin detected - skipping entitlements');

      // Store user data to localStorage for persistence
      const userData = {
        super_admin: true,
        email: user.Email_Address,
        ...user
      };
      localStorage.setItem('user', JSON.stringify(userData));

      setState((prev) => ({
        ...prev,
        Is_Super_Admin: true,
        User_Entitlements: [],
        Base_User_Entitlements: [],
        Client_ID: null,
        Company_ID: null,
        Has_Client_Context: false,
      }));

      return;
    }

    // Get entitlements for regular users
    const entitlementsResponse = await apiClient.getUserEntitlements({ Email_Address: userEmail });

    if (!entitlementsResponse.success) {
      throw new Error(entitlementsResponse.message || 'Failed to get user entitlements');
    }

    const entitlements = entitlementsResponse.data.Records;
    const firstEntitlement = entitlements[0];

    // Store user data to localStorage for regular users
    const userData = {
      super_admin: false,
      email: user.Email_Address,
      ...user
    };
    localStorage.setItem('user', JSON.stringify(userData));

    setState((prev) => ({
      ...prev,
      Is_Super_Admin: false,
      User_Entitlements: entitlements,
      Base_User_Entitlements: entitlements,
      Client_ID: firstEntitlement?.Client_ID || null,
      Company_ID: firstEntitlement?.Company_ID || null,
      Has_Client_Context: Boolean(firstEntitlement?.Client_ID && firstEntitlement?.Company_ID),
    }));
  }, [apiClient]);

  /**
   * Authenticate user with Cognito DIRECTLY
   * Following Tim-Combo pattern - no custom auth endpoints
   */
  const authenticate = useCallback(
    async (email: string, password: string) => {
      if (!userPool) {
        setState((prev) => ({ ...prev, Error_Message: 'Cognito not configured' }));
        throw new Error('Cognito not configured');
      }

      console.log('[StatusProvider] Starting Cognito authentication for:', email);
      setState((prev) => ({ ...prev, Is_Loading: true, Error_Message: null }));

      try {
        const authenticationData = { Username: email, Password: password };
        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = { Username: email, Pool: userPool };
        const cognitoUser = new CognitoUser(userData);

        // Step 1: Authenticate with Cognito DIRECTLY
        await new Promise<void>((resolve, reject) => {
          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (result: any) => {
              const idToken = result.getIdToken().getJwtToken();
              const accessToken = result.getAccessToken().getJwtToken();
              const refreshToken = result.getRefreshToken().getToken();

              // Store tokens in localStorage (Tim-Combo pattern)
              // Use configured token storage key
              if (tokenStorageKey === 'token') {
                localStorage.setItem('token', idToken);
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
              } else {
                localStorage.setItem('idToken', idToken);
                localStorage.setItem('accessToken', accessToken);
              }
              localStorage.setItem('email_address', email);

              console.log('[StatusProvider] Cognito authentication successful');
              resolve();
            },
            onFailure: (err: any) => {
              console.error('[StatusProvider] Cognito authentication failed:', err);
              reject(err);
            },
            newPasswordRequired: (userAttributes: any) => {
              // Handle new password required challenge
              console.log('[StatusProvider] New password required');
              reject(new Error('Password change required. Please contact support.'));
            },
          });
        });

        // Step 2: Fetch user data and entitlements from API
        await Check_User_Status(email);

        // Step 3: Update auth status (triggers routing via useEffect in SignIn)
        setState((prev) => ({
          ...prev,
          Auth_Status: true,
          Is_Loading: false,
          Email_Address: email,
        }));

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        console.error('[StatusProvider] Authentication error:', errorMessage);
        setState((prev) => ({
          ...prev,
          Error_Message: errorMessage,
          Auth_Status: false,
          Is_Loading: false,
        }));
        throw error;
      }
    },
    [userPool, tokenStorageKey, Check_User_Status]
  );

  /**
   * Logout user - clear tokens and state
   */
  const logout = useCallback(() => {
    console.log('[StatusProvider] Logging out');

    // Clear all tokens (support both token storage patterns)
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('email_address');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedClientId');
    localStorage.removeItem('selectedCompanyId');

    // Reset state
    setState({
      Is_Super_Admin: false,
      User_Entitlements: [],
      Base_User_Entitlements: [],
      Is_Loading: false,
      Auth_Status: false,
      Company_ID: null,
      Client_ID: null,
      Client_Name: null,
      Email_Address: null,
      Has_Client_Context: false,
      Error_Message: null,
    });

    navigate('/login');
  }, [navigate]);

  /**
   * Set client context for super admin
   * Creates synthetic entitlements based on selected client
   * Only available if enableClientContext is true and getClient is provided
   */
  const Set_Client_Context = enableClientContext && apiClient.getClient ? useCallback(
    async (clientId: string, companyId: string, options?: { navigate?: boolean }) => {
      console.log('[StatusProvider] Setting client context:', { clientId, companyId });

      if (!state.Is_Super_Admin) {
        throw new Error('Only super admins can set client context');
      }

      if (!apiClient.getClient) {
        throw new Error('Client API not available');
      }

      try {
        // Fetch client details
        const clientResponse = await apiClient.getClient({ id: clientId });

        if (!clientResponse.success) {
          throw new Error('Failed to fetch client');
        }

        const client = clientResponse.data.Records[0];

        // Create synthetic entitlement for super admin in this client context
        const syntheticEntitlement: any = {
          ID: `synthetic-${clientId}-${Date.now()}`,
          Email_Address: state.Email_Address || '',
          Client_ID: clientId,
          Company_ID: companyId,
          Admin: true,
          Developer: true,
          Approver: true,
          Executive: true,
          Recorder: true,
          Manager: true,
          Auditor: true,
        };

        setState((prev) => ({
          ...prev,
          Client_ID: clientId,
          Company_ID: companyId,
          Client_Name: client?.Client_Name || null,
          Has_Client_Context: true,
          User_Entitlements: [syntheticEntitlement],
        }));

        if (options?.navigate !== false) {
          navigate('/admin/dashboard');
        }
      } catch (error) {
        console.error('[StatusProvider] Error setting client context:', error);
        throw error;
      }
    },
    [state.Is_Super_Admin, state.Email_Address, apiClient.getClient, navigate]
  ) : undefined;

  /**
   * Clear client context (return to super admin view)
   * Only available if enableClientContext is true
   */
  const Clear_Client_Context = enableClientContext ? useCallback(() => {
    console.log('[StatusProvider] Clearing client context');
    setState((prev) => ({
      ...prev,
      Client_ID: null,
      Company_ID: null,
      Client_Name: null,
      Has_Client_Context: false,
      User_Entitlements: [],
    }));
    navigate('/super/dashboard');
  }, [navigate]) : undefined;

  // Auto-restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem(tokenStorageKey);
      if (!token) return;

      const email = localStorage.getItem('email_address');
      if (email) {
        try {
          setState((prev) => ({ ...prev, Is_Loading: true }));
          await Check_User_Status(email);
          setState((prev) => ({
            ...prev,
            Auth_Status: true,
            Email_Address: email,
            Is_Loading: false,
          }));
        } catch (error) {
          console.error('[StatusProvider] Session restore failed:', error);
          logout();
        }
      }
    };

    restoreSession();
  }, [tokenStorageKey, Check_User_Status, logout]);

  const value: StatusContextValue = {
    ...state,
    authenticate,
    logout,
    Check_User_Status,
    ...(Set_Client_Context && { Set_Client_Context }),
    ...(Clear_Client_Context && { Clear_Client_Context }),
  };

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>;
}

export default StatusProvider;
